import { useState, useRef, useMemo, useEffect } from 'react';
import styles from './Menu.module.css';
import { MenuProps, MenuItemConfig } from './types';
import { useClickOutside } from '../hooks/useClickOutside';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation';

const MenuItem: React.FC<{
  item: MenuItemConfig;
  isFocused?: boolean;
  onItemClick: (item: MenuItemConfig) => void;
  onMouseEnter?: () => void;
}> = ({ item, isFocused, onItemClick, onMouseEnter }) => {
  return (
    <div
      className={`${styles.menuItem} ${isFocused ? styles.focused : ''}`}
      onClick={() => onItemClick(item)}
      onMouseEnter={onMouseEnter}
      role="menuitem"
      aria-haspopup={item.submenu ? 'menu' : undefined}
      tabIndex={-1}
    >
      {item.label}
    </div>
  );
};

const Menu: React.FC<MenuProps> = ({ menuDisplayName, items = [], id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = () => {
    setIsOpen(false);
    setHoveredIndex(null);
    setFocusedIndex(-1);
    setNavigationPath([]);
  };

  useClickOutside(menuRef, isOpen, closeMenu);

  const handleItemSelect = (item: MenuItemConfig, _index: number, _path: number[]) => {
    if (!item.submenu) {
      item.onClick?.();
      closeMenu();
    }
  };

  const handleItemClick = (item: MenuItemConfig) => {
    handleItemSelect(item, 0, []);
  };

  const handleSubmenuItemClick = (subItem: MenuItemConfig) => {
    subItem.onClick?.();
    closeMenu();
  };

  // Transform items to add children property for keyboard navigation hook compatibility
  const transformedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        children: item.submenu,
      })),
    [items],
  );

  const { focusedIndex, navigationPath, setFocusedIndex, setNavigationPath } =
    useKeyboardNavigation({
      isOpen,
      items: transformedItems,
      onItemSelect: handleItemSelect,
      onClose: closeMenu,
      containerRef: menuRef,
      hierarchical: true,
    });

  // Synchronize keyboard navigation with mouse hover state
  useEffect(() => {
    if (navigationPath.length === 0 && focusedIndex >= 0) {
      // When at root level with keyboard focus, clear hover to prevent submenu showing
      setHoveredIndex(null);
    }
  }, [focusedIndex, navigationPath]);

  return (
    <div ref={menuRef} className={styles.menuContainer}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid={id}
        className={styles.menuButton}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${id}-menu` : undefined}
      >
        {menuDisplayName}
      </button>

      {isOpen && (
        <div className={styles.dropdown} role="menu" id={`${id}-menu`} aria-labelledby={id}>
          {items.map((item, index) => (
            <div
              key={item.id}
              className={styles.menuItemWrapper}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <MenuItem
                item={item}
                isFocused={index === focusedIndex && navigationPath.length === 0}
                onItemClick={handleItemClick}
                onMouseEnter={() => setHoveredIndex(index)}
              />
              {/* Show submenu on hover or keyboard navigation */}
              {item.submenu &&
                (hoveredIndex === index ||
                  (navigationPath.length > 0 && navigationPath[0] === index)) && (
                  <div className={styles.submenu} role="menu" aria-label={`${item.label} submenu`}>
                    {item.submenu.map((subItem, subIndex) => (
                      <div
                        key={subItem.id}
                        className={`${styles.menuItem} ${navigationPath.length === 1 && focusedIndex === subIndex ? styles.focused : ''}`}
                        onClick={() => handleSubmenuItemClick(subItem)}
                        role="menuitem"
                        tabIndex={-1}
                      >
                        {subItem.label}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Menu;
